/**
 * Registers the shell's actions (actions/registry.ts) and binds the active
 * mode's keymap. Nav actions (`nav.<module>`, `nav.<module>.<route>`) come
 * from the manifests (`buildNavActions`), before the side nav collapses the
 * admin pages, so every page a familiar menu names resolves (ADM-14). A
 * page the person may not open keeps its permission: the familiar modes
 * grey it with the reason, Velora Modern hides it.
 */
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import {
  findAction,
  registerAction,
  runAction,
  setActionPermissionCheck,
} from '../actions/registry.js';
import { useOfficeScope } from '../office/OfficeScope.js';
import { usePrefs } from '../prefs/prefs.js';
import { usePrivacy } from '../privacy/privacy.js';
import { canInScope } from '../session/access.js';
import { useSession } from '../session/session.js';
import { useSkin, useTerms } from '../skins/SkinProvider.js';
import { registerShortcut } from '../shortcuts/registry.js';
import type { NavActionSpec } from './nav.js';

export interface SkinBindingsProps {
  navActions: readonly NavActionSpec[];
  onPalette: () => void;
  onShortcutSheet: () => void;
  onOfficeMenu: () => void;
}

export function SkinBindings({
  navActions,
  onPalette,
  onShortcutSheet,
  onOfficeMenu,
}: SkinBindingsProps) {
  const navigate = useNavigate();
  const prefs = usePrefs();
  const privacy = usePrivacy();
  const skin = useSkin();
  const t = useTerms();
  const live = useRef({ navigate, prefs, privacy, onPalette, onShortcutSheet, onOfficeMenu });
  live.current = { navigate, prefs, privacy, onPalette, onShortcutSheet, onOfficeMenu };

  const session = useSession();
  const office = useOfficeScope();

  /* Keys and in-window buttons honour the same permissions the chrome greys. */
  useEffect(() => {
    setActionPermissionCheck((p) => canInScope(session, p, office.locationIds));
    return () => setActionPermissionCheck(null);
  }, [session, office.locationIds]);

  /* Nav actions. */
  useEffect(() => {
    const offs = navActions.map((n) =>
      registerAction({
        id: n.id,
        label: n.label,
        ...(n.permission ? { permission: n.permission } : {}),
        run: () => void live.current.navigate({ to: n.href }),
      }),
    );
    return () => offs.forEach((off) => off());
  }, [navActions]);

  /* Shell verbs. */
  useEffect(() => {
    const L = () => live.current;
    const offs = [
      registerAction({
        id: 'shell.palette',
        label: 'Search or jump to',
        icon: 'search',
        run: () => L().onPalette(),
      }),
      registerAction({
        id: 'shell.shortcuts',
        label: 'Keyboard shortcuts',
        icon: 'keyboard',
        run: () => L().onShortcutSheet(),
      }),
      registerAction({
        id: 'shell.privacy',
        label: 'Privacy mode',
        icon: 'eye-off',
        run: () => L().privacy.toggle(),
      }),
      registerAction({
        id: 'shell.office',
        label: 'Switch office',
        icon: 'building-2',
        run: () => L().onOfficeMenu(),
      }),
      registerAction({
        id: 'shell.home',
        label: t('home'),
        icon: 'layout-dashboard',
        run: () => void L().navigate({ to: '/home' }),
      }),
      registerAction({
        id: 'shell.appearance',
        label: 'Appearance and interface mode',
        icon: 'settings',
        run: () => void L().navigate({ to: '/settings/appearance' }),
      }),
      registerAction({
        id: 'shell.toggleMode',
        label: 'Switch light or dark',
        icon: 'moon',
        run: () => L().prefs.setMode(L().prefs.mode === 'dark' ? 'light' : 'dark'),
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [t]);

  /* The mode's keymap, on top of Velora's shortcuts. */
  useEffect(() => {
    const offs = skin.keymap.map((b) =>
      registerShortcut({
        id: `skin:${skin.layers.keymap}:${b.keys}`,
        keys: b.keys,
        description: findAction(b.action)?.label ?? b.action,
        section: 'Interface mode',
        ...(b.allowInInputs ? { allowInInputs: true } : {}),
        run: () => {
          runAction(b.action);
        },
      }),
    );
    return () => offs.forEach((off) => off());
  }, [skin.keymap, skin.layers.keymap]);

  return null;
}
